export interface ObjectPathFieldItem {
  label: string;
  /**
   * Path to the value in the plan object or custom extractor function.
   */
  path: string | ((plan: Record<string, unknown>) => unknown);
}

export interface ObjectPathFieldGroup {
  title: string;
  items: ObjectPathFieldItem[];
}



/**
 * Build a resolver that extracts values from plan objects using the specified path.
 */
export const buildKeyedPathResolver = <T extends object>(plan: T) => {
  /** Utility to safely extract value by dot-separated path or custom extractor */
  const extractValue = (
    path: string | ((pl: T) => unknown),
    currentPlan: T
  ) => {
    if (typeof path === 'function') {
      return path(currentPlan);
    }

    return resolveByDotPath(currentPlan, path);
  };

  /**
   * Resolve a dot-separated path against an object.
   * Handles keys that contain dots (e.g. "21-40 sq. cm second degree")
   * by greedily trying longer key segments before splitting further.
   */
  const resolveByDotPath = (obj: unknown, remaining: string): unknown => {
    if (!remaining) return obj;
    if (obj == null || typeof obj !== 'object') return undefined;
    const record = obj as Record<string, unknown>;

    // Try matching from the longest possible key to the shortest
    const dotIndices: number[] = [];
    for (let i = 0; i < remaining.length; i++) {
      if (remaining[i] === '.') dotIndices.push(i);
    }

    // Try exact full remaining as key
    if (remaining in record) return record[remaining];

    // Try splitting at each dot position (left to right = shortest key first)
    for (const idx of dotIndices) {
      const key = remaining.substring(0, idx);
      if (key in record) {
        const result = resolveByDotPath(record[key], remaining.substring(idx + 1));
        if (result !== undefined) return result;
      }
    }

    return undefined;
  };

  return (
    item: ObjectPathFieldItem
  ): { value: string | number | '—'; keyUsed: string } => {
    const val = extractValue(item.path as string | ((pl: T) => unknown), plan);
    if (val !== undefined && val !== null && val !== '') {
      return { value: (val as unknown) as string | number, keyUsed: 'path' };
    }

    return { value: '—', keyUsed: 'none' };
  };
};